import { isReadingAnswerComplete } from 'components/questions/PassagePlayer';
import type { QuestionAnswer, PassageAnswer } from '@examing/types';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flag, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { isMarketplaceTest } from 'modules/test/marketplace-policy';
import { Checkbox } from 'components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { Input } from 'components/ui/input';
import { useToast } from 'components/ui/use-toast';
import {
    FillInBlanksPlayer,
    ReorderPlayer,
    OpenEndedPlayer,
    PassagePlayer,
    MatchingPlayer,
} from 'components/questions';
import { ExamTopBar } from 'components/exam/ExamTopBar';
import { ExamStateCard } from 'components/exam/ExamStateCard';
import {
    EXAM_CHECKBOX_CLASS,
    EXAM_RADIO_CLASS,
    ExamOptionRow,
    optionLetter,
} from 'components/exam/ExamOptionRow';
import { resolveExamClock } from 'components/exam/ExamTimer';
import { ExamPhoneHeader } from 'components/exam/ExamPhoneHeader';
import { ExamPhoneNav } from 'components/exam/ExamPhoneNav';
import { ViolationBanner } from 'components/exam/ViolationBanner';
import { PaletteDrawer } from 'components/exam/PaletteDrawer';
import { PaletteSheet } from 'components/exam/PaletteSheet';
import { SubmitDialog } from 'components/exam/SubmitDialog';
import { expandedSubQuestions, questionItemCount, questionNumbering } from 'components/exam/questionNumbering';
import { ResultDialog } from 'components/exam/ResultDialog';
import { useMobileDockOverlay } from 'components/mobile/MobileShell';
import { useScrollTopOnChange } from 'hooks/use-scroll-top-on-change';
import * as AttemptModule from 'modules/attempt';
import * as TestModule from 'modules/test';
import { clearResumePosition, loadResumePosition, saveResumePosition } from 'modules/attempt/resumePosition';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { SessionEndDialogs, useSessionEndFlow } from 'modules/sessionEnd';
import { useSetBalance } from 'modules/portal-plans/useSetBalance';
import { useAuthContext } from 'modules/auth/context';
import { publicQuestionsCount } from 'modules/test/public-question-count';
import { TestStartScreen } from 'components/test/TestStartScreen';
import { TestStartPayment } from 'components/portal/TestStartPayment';
import { PortalPaymentReturn } from 'components/portal/PortalPaymentReturn';
import { RenderedHtml } from 'modules/editor';
import { ReportQuestionButton } from 'modules/questionReports';
import { isInsideDialog } from 'components/exam/isInsideDialog';
import { StandardizedExamRunner } from 'modules/standardizedExam/components/runner/StandardizedExamRunner';
import { ExamFinishedScreen } from 'modules/standardizedExam/components/finished/ExamFinishedScreen';
import { ExamReadinessCard } from 'modules/standardizedExam/components/start/ExamReadinessCard';
import { normalizeCategorySlug } from 'modules/test/public-categories';
import { SprInput } from 'modules/standardizedExam/components/runner/SprInput';
import { PassageHighlighter } from 'modules/standardizedExam/components/runner/PassageHighlighter';
import { clearHighlights } from 'modules/standardizedExam/components/runner/highlightStore';
import { usePassageHighlights } from 'modules/attempt/usePassageHighlights';
import { PORTAL_PLAN_PICKER_PATH } from 'modules/portal-plans/paths';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { getQuestionOptionLabel } from 'lib/questionOptions';
import config from 'config';

interface Answer {
    questionId: string;
    value: QuestionAnswer;
}

// Maps each question type to its i18n label key (shown next to the question
// number). Falls back to 'question' for anything unmapped.
const QUESTION_TYPE_LABEL_KEY: Record<string, string> = {
    MULTIPLE_CHOICE: 'single_choice',
    MULTIPLE_RESPONSE: 'multiple_response',
    TRUE_FALSE: 'true_false',
    SHORT_ANSWER: 'short_answer',
    MATCHING: 'matching',
    FILL_IN_BLANKS: 'fill_in_blanks',
    OPEN_ENDED: 'open_ended',
    REORDER: 'reorder',
    PASSAGE: 'passage',
};

// Types whose player renders the prompt itself (with inline blanks or selectable
// text), so the standalone prompt above must be hidden to avoid showing raw
// {{id}} markers twice.
const PROMPT_OWNED_TAKE_TYPES = new Set([
    'FILL_IN_BLANKS',
    // PassagePlayer renders the reading passage (passageText) + its own
    // instruction line; the standalone prompt would repeat the passage text,
    // showing it twice.
    'PASSAGE',
]);

interface TestTakingProps {
    /** No-account guest mode: rehydrate from the session token, no app chrome. */
    guest?: boolean;
}


/** The author org's plan no longer covers this test: a localized, neutral message for the candidate. */
function planUnavailableText(error: unknown, t: (key: string, fallback: string) => string): string | null {
    return (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'TEST_PLAN_UNAVAILABLE'
        ? t('test_plan_unavailable', "This test isn't available right now. Please contact the test organizer.")
        : null;
}

const TestTaking: React.FC<TestTakingProps> = ({ guest = false }) => {
    const { t } = useTranslation();
    const { id: testId, attemptId: attemptIdFromUrl } = useParams<{
        id: string;
        attemptId?: string;
    }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const inviteCode = searchParams.get('invite') || searchParams.get('code');
    // Payme returns a top-up for this test here with its order id (see portal billing).
    const paymentOrder = searchParams.get('paymentOrder');
    const { toast } = useToast();
    const isPreviewMode = pathname.includes('/preview/');
    // The take-test flow is mounted under both shells: staff/author at /app/tests/take
    // and candidates at /portal/tests/take (sealed inside their portal). Derive the
    // base from the URL so session promotion and exits stay within the right shell.
    const isPortal = pathname.startsWith('/portal');
    const basePath = isPortal ? '/portal' : '/app';
    const backToTestsPath = isPortal ? '/portal' : '/app/tests';

    const [isLoading, setIsLoading] = useState(true);
    const [isStarted, setIsStarted] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Map<string, Answer>>(new Map());
    const [flaggedQuestions, setFlaggedQuestions] = useState<Set<string>>(new Set());
    // Answer eliminator (on per subject by the admin): options struck out, by question id.
    const [eliminatorOn, setEliminatorOn] = useState(false);
    const [eliminated, setEliminated] = useState<Map<string, Set<string>>>(new Map());
    // A reload returns to the question the candidate was on, with their flags and struck options.
    const restorePosition = useCallback((id: string, questions: Array<{ id: string }>) => {
        const saved = loadResumePosition(id, questions.map((q) => q.id));
        if (saved.index !== null) setCurrentQuestionIndex(saved.index);
        if (saved.flagged.length) setFlaggedQuestions(new Set(saved.flagged));
        const struck = Object.entries(saved.eliminated);
        if (struck.length) setEliminated(new Map(struck.map(([questionId, options]) => [questionId, new Set(options)])));
    }, []);
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attemptResult, setAttemptResult] = useState<any>(null);
    const [tabSwitchCount, setTabSwitchCount] = useState(0);
    // Mobile-only: the question palette is a bottom-sheet rather than a sidebar.
    const [paletteOpen, setPaletteOpen] = useState(false);

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    // Per-question autosave debounce, keyed by questionId. A single shared
    // timeout would let answering a second question cancel the still-pending
    // save for a different one — keying by id makes every question's debounce
    // independent. Failed saves are left in the map (not deleted) so a later
    // flush — e.g. on submit retry — can attempt them again.
    const pendingSavesRef = useRef<Map<string, { timeout: ReturnType<typeof setTimeout>; value: Answer['value'] }>>(
        new Map(),
    );
    // Guards the timer-expiry auto-submit against firing more than once (React
    // 18 StrictMode double-invokes state updater functions in dev).
    const hasAutoSubmittedRef = useRef(false);
    // Set synchronously the moment a real submit starts so a concurrent caller
    // (the countdown's zero-branch firing while a manual submit is mid-flight)
    // can't fire a second submitAttempt for the same attempt. A ref, not state,
    // because the timer callback needs the guard to be true immediately.
    const isSubmittingRef = useRef(false);
    // Latest keyboard handler, kept in a ref so the window listener subscribes
    // once (on start) yet always sees current state — same pattern as logEventRef.
    const keyboardHandlerRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
    // Auto-submit-on-timer-expiry, reassigned every render (same pattern as
    // questionExpiryRef below) so the timer effect — whose deps are
    // intentionally restricted to [isStarted, hasTimeLimit] to avoid tearing
    // down/rebuilding the interval on every render — still always invokes the
    // current handleSubmit closure instead of a stale one.
    const autoSubmitRef = useRef<() => void>(() => undefined);

    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [test, setTest] = useState<AttemptModule.Types.TestForAttempt | null>(null);
    const [allQuestions, setAllQuestions] = useState<AttemptModule.Types.Question[]>([]);
    // Set when a numbered passage sub-question is picked in the palette; seq lets the same one be picked twice.
    // Reading highlights on passage questions; kept per attempt in this browser.
    const passageHighlights = usePassageHighlights(isPreviewMode ? null : attemptId);
    const [subQuestionFocus, setSubQuestionFocus] = useState<{ questionId: string; seq: number }>();

    // Respondent start screen: shown before the attempt (and any invite) is
    // consumed. A resumable in-progress attempt skips straight past it.
    const [startInfo, setStartInfo] = useState<AttemptModule.Types.IApi.StartInfo.Response | null>(
        null,
    );
    const [showStartScreen, setShowStartScreen] = useState(false);
    const [startError, setStartError] = useState<string | null>(null);
    // A paid test is bought in a payment step before its attempt is created;
    // the details from the start screen wait here until the purchase lands.
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [examFinished, setExamFinished] = useState(false);
    const pendingStartRef = useRef<{ identification?: Record<string, string>; consentAccepted?: boolean }>({});
    const { state: authState } = useAuthContext();
    const setBalance = useSetBalance();

    const startAttempt = AttemptModule.Hooks.useStart();
    const { data: subscription } = usePortalSubscription();
    // After a hand-in: analysing → plan offer → feedback, each only for those it applies to.
    const sessionEnd = useSessionEndFlow({ isPortal, isGuest: guest, isPreview: isPreviewMode });
    const saveAnswer = AttemptModule.Hooks.useSaveAnswer();
    const submitAttempt = AttemptModule.Hooks.useSubmit();
    const logEvent = AttemptModule.Hooks.useLogEvent();

    const logEventRef = useRef(logEvent);
    const toastRef = useRef(toast);
    logEventRef.current = logEvent;
    toastRef.current = toast;

    const flattenQuestions = useCallback((testData: AttemptModule.Types.TestForAttempt) => {
        const ordered = [...testData.sections].sort((a, b) => a.order - b.order);
        return ordered.flatMap((section) => section.questions);
    }, []);

    // Preview (author): load the test without an attempt, then show the same
    // start screen respondents see. "Start" just flips into the in-test UI.
    const loadPreview = useCallback(async () => {
        if (!testId) return;
        setIsLoading(true);
        try {
            const response = await AttemptModule.Api.Preview(testId);
            const testData = AttemptModule.Mappers.TestForAttempt(response.data);
            setTest(testData);
            setAllQuestions(flattenQuestions(testData));
            const timeLimit = testData.settings?.timeLimit;
            if (timeLimit) setTimeRemaining(timeLimit * 60);
            setStartInfo({
                test: {
                    id: testData.id,
                    title: testData.title,
                    description: testData.description || '',
                    totalQuestions: testData.totalQuestions,
                    timeLimit: testData.settings?.timeLimit ?? null,
                    priceSom: 0,
                    purchased: false,
                    sectionsCount: testData.sections.length,
                },
                startPage: AttemptModule.Mappers.StartPageConfig(testData.startPage),
                attemptsUsed: 0,
                attemptsAllowed: null,
                resumable: false,
            });
            setShowStartScreen(true);
        } catch (error: any) {
            toast.error(t('error'), {
                description: error?.response?.data?.message || 'Failed to load preview',
            });
            navigate('/app/tests');
        } finally {
            setIsLoading(false);
        }
    }, [testId, flattenQuestions, toast, navigate, t]);

    const handleStart = useCallback(async (
        identification?: Record<string, string>,
        consentAccepted?: boolean,
    ) => {
        if (!testId) return;
        // Don't hide the start screen up-front: on a failed start (e.g. an
        // invite consumed between viewing and starting) the `!isStarted` guard
        // keeps the start screen visible so the respondent sees the error and
        // can retry, rather than dropping to a dead end. Success flips
        // `isStarted`, which hides the start screen branch.
        setIsLoading(true);
        try {
            const result = await startAttempt.mutateAsync({
                testId,
                inviteCode: inviteCode || undefined,
                identification,
                consentAccepted,
            });
            setAttemptId(result.attempt.id);
            setTest(result.test);

            // Promote the URL to the session-scoped form so the attempt has a
            // bookmarkable identity. If the user landed at /tests/take/<id> we
            // swap to /tests/take/<id>/sessions/<attemptId> (replace, so back
            // button still returns to wherever they launched from). If they
            // already had a session URL but for a different attemptId — e.g.
            // a stale link — we point them at the canonical one returned by
            // the resume-first server logic. Preview mode keeps the original
            // URL so re-entering preview doesn't get scoped to a stale id.
            if (!isPreviewMode && attemptIdFromUrl !== result.attempt.id) {
                const canonicalSessionPath = `${basePath}/tests/take/${testId}/sessions/${result.attempt.id}`;
                const query = new URLSearchParams(searchParams);
                query.delete('paymentOrder');
                const preserveQuery = query.toString();
                const target = preserveQuery
                    ? `${canonicalSessionPath}?${preserveQuery}`
                    : canonicalSessionPath;
                navigate(target, { replace: true });
            }

            const questions = flattenQuestions(result.test);
            setAllQuestions(questions);

            // IELTS/SAT runs are timed per section by the standardized runner.
            const timeLimit = result.test.examConfig ? undefined : result.test.settings?.timeLimit;
            if (timeLimit) {
                const totalSeconds = timeLimit * 60;
                const startedAt = result.attempt.startedAt
                    ? new Date(result.attempt.startedAt).getTime()
                    : Date.now();
                const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
                const remaining = Math.max(0, totalSeconds - elapsedSeconds);
                setTimeRemaining(remaining);
            }

            const hadExistingAnswers = result.attempt.answers && result.attempt.answers.length > 0;
            if (hadExistingAnswers) {
                const existingAnswers = new Map<string, Answer>();
                result.attempt.answers.forEach((answer) => {
                    existingAnswers.set(answer.questionId, {
                        questionId: answer.questionId,
                        value: answer.value,
                    });
                });
                setAnswers(existingAnswers);
                const firstUnanswered = questions.findIndex((q) => !existingAnswers.has(q.id));
                if (firstUnanswered >= 0) setCurrentQuestionIndex(firstUnanswered);
            }
            restorePosition(result.attempt.id, questions);
            setIsStarted(true);

            if (timeLimit && hadExistingAnswers) {
                const totalSeconds = timeLimit * 60;
                const startedAt = result.attempt.startedAt
                    ? new Date(result.attempt.startedAt).getTime()
                    : Date.now();
                const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
                if (elapsedSeconds >= totalSeconds) {
                    submitAttempt.mutate({ attemptId: result.attempt.id });
                }
            }
        } catch (error: any) {
            const data = error?.response?.data;
            // The start charged the balance and it fell short (the purchase was
            // spent elsewhere, or the price was unknown): open the payment step
            // instead of leaving a dead-end error.
            if (data?.code === 'INSUFFICIENT_BALANCE' && !isPreviewMode) {
                setBalance(data.balanceSom);
                setStartInfo((info) =>
                    info && {
                        ...info,
                        test: { ...info.test, priceSom: Number(data.requiredSom) || info.test.priceSom, purchased: false },
                    },
                );
                pendingStartRef.current = { identification, consentAccepted };
                setPaymentOpen(true);
                return;
            }
            toast.error(t('error'), {
                description: error?.message || 'Failed to start the test',
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        testId,
        inviteCode,
        startAttempt,
        submitAttempt,
        flattenQuestions,
        toast,
        t,
        navigate,
        isPreviewMode,
        attemptIdFromUrl,
        searchParams,
        basePath,
        restorePosition,
        setBalance,
    ]);

    // Real respondent: fetch the read-only start info first. If they already
    // have an in-progress attempt, resume straight in; otherwise show the
    // start screen so identification/consent are collected before any attempt
    // (or invite) is consumed.
    const loadStartInfo = useCallback(async () => {
        if (!testId) return;
        setIsLoading(true);
        try {
            const { data } = await AttemptModule.Api.StartInfo(testId, inviteCode || undefined);
            const info = AttemptModule.Mappers.StartInfo(data);
            if (info.resumable) {
                await handleStart();
                return;
            }
            setStartInfo(info);
            setShowStartScreen(true);
        } catch (error: any) {
            setStartError(
                planUnavailableText(error, t) || error?.response?.data?.message || t('this_test_is_not_available'),
            );
        } finally {
            setIsLoading(false);
        }
    }, [testId, inviteCode, handleStart, t]);

    // Guest mode: rehydrate the session bound to the guest token. Identification
    // was already collected on the /s/<token> page, so there is no start screen —
    // we drop straight into the in-test UI (or the result, if already finished).
    const loadGuestSession = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await AttemptModule.Api.GuestSession();
            const result = AttemptModule.Mappers.StartResponse(data);
            setAttemptId(result.attempt.id);
            setTest(result.test);
            const questions = flattenQuestions(result.test);
            setAllQuestions(questions);

            // IELTS/SAT runs are timed per section by the standardized runner.
            const timeLimit = result.test.examConfig ? undefined : result.test.settings?.timeLimit;
            if (timeLimit) {
                const totalSeconds = timeLimit * 60;
                const startedAt = result.attempt.startedAt
                    ? new Date(result.attempt.startedAt).getTime()
                    : Date.now();
                const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
                setTimeRemaining(Math.max(0, totalSeconds - elapsedSeconds));
            }

            if (result.attempt.answers && result.attempt.answers.length > 0) {
                const existing = new Map<string, Answer>();
                result.attempt.answers.forEach((a) =>
                    existing.set(a.questionId, { questionId: a.questionId, value: a.value }),
                );
                setAnswers(existing);
                const firstUnanswered = questions.findIndex((q) => !existing.has(q.id));
                if (firstUnanswered >= 0) setCurrentQuestionIndex(firstUnanswered);
            }
            restorePosition(result.attempt.id, questions);

            // A finished session has no in-test UI to show — route to the result.
            const rt = (data as any)?.attempt?.resultToken;
            if (result.attempt.status !== 'IN_PROGRESS') {
                navigate(rt ? `/r/${rt}` : '/', { replace: true });
                return;
            }
            setIsStarted(true);
        } catch (error: any) {
            setStartError(planUnavailableText(error, t) || error?.response?.data?.message || t('this_test_is_not_available'));
        } finally {
            setIsLoading(false);
        }
    }, [flattenQuestions, navigate, t, restorePosition]);

    // Bridge from the start screen into the session. Preview just flips the UI;
    // a real run creates the attempt with the collected identification/consent.
    const beginSession = useCallback(
        (identification: Record<string, string>, consentAccepted: boolean) => {
            if (isPreviewMode) {
                setShowStartScreen(false);
                setIsStarted(true);
                return;
            }
            // Not bought yet: take the payment first; the start then spends the purchase.
            if (startInfo && startInfo.test.priceSom > 0 && !startInfo.test.purchased) {
                pendingStartRef.current = { identification, consentAccepted };
                setPaymentOpen(true);
                return;
            }
            handleStart(identification, consentAccepted);
        },
        [isPreviewMode, handleStart, startInfo],
    );

    const handlePurchased = useCallback(() => {
        setPaymentOpen(false);
        setStartInfo((info) => info && { ...info, test: { ...info.test, purchased: true } });
        const { identification, consentAccepted } = pendingStartRef.current;
        handleStart(identification, consentAccepted);
    }, [handleStart]);

    const dismissPaymentReturn = useCallback(() => {
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.delete('paymentOrder');
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    useEffect(() => {
        if (isPreviewMode) loadPreview();
        else if (guest) loadGuestSession();
        else loadStartInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!testId || isPreviewMode) return;
        const source = searchParams.get('from') === 'landing' ? 'LANDING' : 'PORTAL';
        void TestModule.Api.RecordTestView(testId, source);
    }, [testId, isPreviewMode, searchParams]);

    // IELTS-style / Digital SAT practice attempts run in the section runner.
    // Previews keep the plain view so authors can read every section.
    const runsStandardizedExam = Boolean(test?.examConfig) && !isPreviewMode && isStarted;

    // The question view brings its own phone bottom bar, so hide the Studio phone
    // dock while it is up (no-op outside the phone shell and for IELTS/SAT runs).
    useMobileDockOverlay(isStarted && !runsStandardizedExam && !!test && allQuestions.length > 0);

    // The runner finalized the attempt (last section closed): go to the result.
    const finishStandardizedExam = useCallback(async () => {
        if (guest) {
            try {
                const { data } = await AttemptModule.Api.GuestSession();
                sessionStorage.removeItem(config.api.guest_token_key);
                const rt = (data as any)?.attempt?.resultToken;
                navigate(rt ? `/r/${rt}` : '/', { replace: true, state: { justFinished: true } });
            } catch {
                navigate('/', { replace: true });
            }
            return;
        }
        // A signed-in IELTS candidate lands on the "finished" page first: what is marked, what is still being graded.
        if (attemptId && test?.examConfig?.standard === 'IELTS') {
            setExamFinished(true);
            return;
        }
        if (attemptId) navigate(`${basePath}/results/${attemptId}`, { replace: true });
    }, [guest, attemptId, basePath, navigate, test?.examConfig?.standard]);

    const hasTimeLimit = timeRemaining !== null;
    useEffect(() => {
        if (isStarted && hasTimeLimit && timeRemaining && timeRemaining > 0) {
            timerRef.current = setInterval(() => {
                setTimeRemaining((prev) => {
                    if (prev === null || prev <= 1) {
                        // Stop the interval as soon as the clock hits zero —
                        // otherwise it keeps firing every second forever (prev
                        // stays 0, so this branch re-triggers on every tick),
                        // repeatedly re-submitting the attempt.
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                            timerRef.current = null;
                        }
                        if (!hasAutoSubmittedRef.current) {
                            hasAutoSubmittedRef.current = true;
                            // Route through handleSubmit (same path the manual
                            // submit button and per-question expiry use) so the
                            // candidate actually lands on the result screen
                            // instead of being stuck on the question view.
                            autoSubmitRef.current();
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStarted, hasTimeLimit]);

    // Per-question pacing (Time settings page, per_question mode). The clock is
    // client-side UX: it restarts for the current question on refresh/resume,
    // while the server-derived aggregate budget above still caps total time.
    const perQuestionSeconds = test?.perQuestionSeconds || null;
    const [questionTimeRemaining, setQuestionTimeRemaining] = useState<number | null>(null);
    const [timeUpFlash, setTimeUpFlash] = useState(false);
    const questionExpiryRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (!isStarted || !perQuestionSeconds || allQuestions.length === 0) return;
        // Deadline-based so a throttled background tab can't stretch the clock.
        const deadline = Date.now() + perQuestionSeconds * 1000;
        let expired = false;
        setQuestionTimeRemaining(perQuestionSeconds);
        const interval = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setQuestionTimeRemaining(remaining);
            if (remaining <= 0 && !expired) {
                expired = true;
                clearInterval(interval);
                questionExpiryRef.current();
            }
        }, 250);
        return () => clearInterval(interval);
        // currentQuestionIndex is the re-arm key: every advance gets a fresh clock.
    }, [isStarted, perQuestionSeconds, currentQuestionIndex, allQuestions.length]);

    // Kept in a ref (same pattern as logEventRef) so the timer effect doesn't
    // re-arm mid-question when the handler's closed-over state changes identity.
    questionExpiryRef.current = () => {
        // The current selection (or lack of one) is already in `answers`, and the
        // debounced auto-save still fires after advancing, so nothing is lost.
        setTimeUpFlash(true);
        window.setTimeout(() => setTimeUpFlash(false), 1500);
        if (currentQuestionIndex >= allQuestions.length - 1) {
            handleSubmit(true);
        } else {
            setCurrentQuestionIndex((prev) => Math.min(prev + 1, allQuestions.length - 1));
        }
    };

    useEffect(() => {
        if (!attemptId || !isStarted || !test?.antiCheating?.antiCheatingMonitor) return;
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setTabSwitchCount((prev) => prev + 1);
                logEventRef.current.mutate({
                    attemptId,
                    type: 'TAB_SWITCH',
                    payload: { timestamp: new Date().toISOString() },
                });
                toastRef.current.error(t('warning'), {
                    description: t('tab_switch_detected_this_activity_has_been_logged'),
                });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [attemptId, isStarted, t, test]);

    useEffect(() => {
        if (!attemptId || !isStarted || !test?.antiCheating?.antiCheatingMonitor) return;
        const handleBlur = () => {
            logEventRef.current.mutate({
                attemptId,
                type: 'FOCUS_LOST',
                payload: { timestamp: new Date().toISOString() },
            });
        };
        window.addEventListener('blur', handleBlur);
        return () => window.removeEventListener('blur', handleBlur);
    }, [attemptId, isStarted, test]);

    // Enforce the per-test anti-cheating restrictions configured on the
    // Anti-cheating settings page. Each handler is attached only when its
    // toggle is on, and torn down on cleanup (CLAUDE.md: always clean up).
    useEffect(() => {
        if (!isStarted || isPreviewMode) return;
        const ac = test?.antiCheating;
        if (!ac) return;
        const cleanups: Array<() => void> = [];

        if (ac.disableRightClick) {
            const onContextMenu = (e: MouseEvent) => e.preventDefault();
            document.addEventListener('contextmenu', onContextMenu);
            cleanups.push(() => document.removeEventListener('contextmenu', onContextMenu));
        }

        if (ac.disableCopyPaste) {
            const block = (e: Event) => e.preventDefault();
            (['copy', 'cut', 'paste'] as const).forEach((evt) => {
                document.addEventListener(evt, block);
                cleanups.push(() => document.removeEventListener(evt, block));
            });
        }

        if (ac.lockdownBrowser) {
            document.documentElement.requestFullscreen?.().catch(() => undefined);
            const onFsChange = () => {
                if (!document.fullscreenElement && attemptId) {
                    logEventRef.current.mutate({
                        attemptId,
                        type: 'FULLSCREEN_EXIT',
                        payload: { timestamp: new Date().toISOString() },
                    });
                }
            };
            document.addEventListener('fullscreenchange', onFsChange);
            cleanups.push(() => {
                document.removeEventListener('fullscreenchange', onFsChange);
                if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
            });
        }

        return () => cleanups.forEach((fn) => fn());
    }, [isStarted, isPreviewMode, test, attemptId]);

    const currentQuestion = allQuestions[currentQuestionIndex];

    // Every question opens at its top — the /app shell, the phone shell and the window all scroll.
    const shellRef = useRef<HTMLDivElement>(null);
    useScrollTopOnChange(currentQuestionIndex, shellRef);

    // Kept in this browser so a reload returns to this question with its flags.
    useEffect(() => {
        if (!attemptId || !isStarted || isPreviewMode) return;
        saveResumePosition(attemptId, {
            questionId: allQuestions[currentQuestionIndex]?.id ?? null,
            flagged: [...flaggedQuestions],
            eliminated: Object.fromEntries([...eliminated].map(([questionId, options]) => [questionId, [...options]])),
        });
    }, [attemptId, isStarted, isPreviewMode, allQuestions, currentQuestionIndex, flaggedQuestions, eliminated]);

    // When the author disables flexible navigation (Time settings page), the
    // candidate is locked to forward-only, one-at-a-time progress and cannot
    // skip an unanswered question. Undefined/true keeps free navigation so
    // existing tests are unaffected. Per-question pacing is always linear (an
    // expired question must not be revisitable), even for configs saved before
    // the server started forcing the flag off.
    const allowFlexibleNav = test?.allowFlexibleNavigation !== false && !perQuestionSeconds;

    const handleAnswerChange = useCallback(
        (questionId: string, value: QuestionAnswer) => {
            setAnswers((prev) => {
                const newAnswers = new Map(prev);
                newAnswers.set(questionId, { questionId, value });
                return newAnswers;
            });
            // Debounce is keyed by questionId, so only this question's own
            // pending save is cancelled/restarted — a different question's
            // pending save is untouched.
            const existing = pendingSavesRef.current.get(questionId);
            if (existing) clearTimeout(existing.timeout);
            const timeout = setTimeout(() => {
                pendingSavesRef.current.delete(questionId);
                if (attemptId && !isPreviewMode) {
                    saveAnswer.mutate({ attemptId, questionId, value });
                }
            }, 500);
            pendingSavesRef.current.set(questionId, { timeout, value });
        },
        [attemptId, isPreviewMode, saveAnswer],
    );

    // Immediately (and synchronously, from the caller's point of view) saves
    // any answers still waiting on their debounce. Called before every submit
    // path so a fast "answer, then submit" sequence within the 500ms debounce
    // window never drops the last edit. Entries are only removed on success —
    // a failed save is left in place so a later flush (e.g. a submit retry)
    // attempts it again instead of silently losing it.
    const flushPendingSaves = useCallback(async () => {
        if (!attemptId || isPreviewMode) {
            pendingSavesRef.current.clear();
            return;
        }
        const entries = Array.from(pendingSavesRef.current.entries());
        if (entries.length === 0) return;
        entries.forEach(([, { timeout }]) => clearTimeout(timeout));
        await Promise.all(
            entries.map(async ([questionId, { value }]) => {
                try {
                    await saveAnswer.mutateAsync({ attemptId, questionId, value });
                    pendingSavesRef.current.delete(questionId);
                } catch {
                    // useSaveAnswer's onError already surfaced a toast; keep the
                    // entry pending so a later flush can retry it.
                }
            }),
        );
    }, [attemptId, isPreviewMode, saveAnswer]);

    const goToQuestion = (index: number, subQuestionId?: string) => {
        if (index < 0 || index >= allQuestions.length) return;
        setCurrentQuestionIndex(index);
        setSubQuestionFocus(
            subQuestionId ? (prev) => ({ questionId: subQuestionId, seq: (prev?.seq ?? 0) + 1 }) : undefined,
        );
    };

    const toggleFlag = (questionId: string) => {
        setFlaggedQuestions((prev) => {
            const newFlags = new Set(prev);
            if (newFlags.has(questionId)) newFlags.delete(questionId);
            else newFlags.add(questionId);
            return newFlags;
        });
    };

    const toggleEliminated = (questionId: string, optionId: string) => {
        setEliminated((prev) => {
            const next = new Map(prev);
            const options = new Set(next.get(questionId));
            if (options.has(optionId)) options.delete(optionId);
            else options.add(optionId);
            if (options.size) next.set(questionId, options);
            else next.delete(questionId);
            return next;
        });
    };

    // Choosing a struck option brings it back, as on the SAT.
    const chooseOption = (questionId: string, optionId: string, value: QuestionAnswer) => {
        if (eliminated.get(questionId)?.has(optionId)) toggleEliminated(questionId, optionId);
        handleAnswerChange(questionId, value);
    };

    const handleSubmit = async (autoSubmit = false) => {
        if (!autoSubmit) {
            setShowSubmitModal(true);
            return;
        }
        setShowSubmitModal(false);
        if (isPreviewMode) {
            setAttemptResult({ preview: true });
            setShowResultModal(true);
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }
        if (!attemptId) return;
        // Reentrancy guard: block a second concurrent submit (e.g. the countdown
        // hitting zero while this request is already in flight).
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        // Stop the countdown now — not after the awaits below — so its zero-branch
        // can't queue an auto-submit during the round-trip.
        hasAutoSubmittedRef.current = true;
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsSubmitting(true);
        try {
            // Flush any answer still waiting on its debounce so it reaches the
            // server before the attempt is finalized.
            await flushPendingSaves();
            const result = await submitAttempt.mutateAsync({ attemptId });
            clearResumePosition(attemptId);
            clearHighlights(attemptId);
            if (guest) {
                // No-account guest: clear the session token and hand off to the
                // public result page (which the result link also points to).
                sessionStorage.removeItem(config.api.guest_token_key);
                const rt = (result as any)?.resultToken;
                navigate(rt ? `/r/${rt}` : '/', { replace: true, state: { justFinished: true } });
                return;
            }
            setAttemptResult(result);
            setShowResultModal(true);
            sessionEnd.begin(attemptId);
        } catch (error: any) {
            toast.error(t('error'), {
                description: error?.message || 'Failed to submit the test',
            });
        } finally {
            setIsSubmitting(false);
            // Release the guard so the user can retry after a failed submit. On
            // success the timer is already stopped and the result screen shows.
            isSubmittingRef.current = false;
        }
    };

    // Reassigned every render (see the declaration above) so timer-expiry
    // auto-submit always calls the current handleSubmit closure.
    autoSubmitRef.current = () => {
        handleSubmit(true);
    };

    const getAnswer = (questionId: string) => answers.get(questionId)?.value;

    const isAnswered = (questionId: string) => {
        const answer = getAnswer(questionId);
        const question = allQuestions.find((item) => item.id === questionId);
        if (question?.type === 'PASSAGE') {
            const values = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer as PassageAnswer : {};
            return Boolean(question.subQuestions?.length) && question.subQuestions!.every((child) => isReadingAnswerComplete(child, values[child.id]));
        }
        if (question?.type === 'MATCHING') return Boolean(question.matchPairs?.length) && question.matchPairs!.every((pair) => Boolean((answer as Record<string, string> | undefined)?.[pair.key]?.trim()));
        if (question?.type === 'FILL_IN_BLANKS') return Boolean(question.blanks?.length) && question.blanks!.every((blank) => Boolean((answer as Record<string, string> | undefined)?.[blank.id]?.trim()));
        if (!answer) return false;
        if (Array.isArray(answer)) return answer.length > 0 && answer.some((a) => a !== '' && a != null);
        if (typeof answer === 'object') {
            const values = Object.values(answer);
            return values.length > 0 && values.some((v) => v !== '' && v != null);
        }
        return answer.toString().trim() !== '';
    };

    const isSubAnswered = (questionId: string, subQuestionId: string) => {
        const question = allQuestions.find((item) => item.id === questionId);
        const answer = getAnswer(questionId);
        const values = answer && typeof answer === 'object' && !Array.isArray(answer) ? (answer as PassageAnswer) : {};
        const child = question?.subQuestions?.find((sub) => sub.id === subQuestionId);
        return !!child && isReadingAnswerComplete(child, values[subQuestionId]);
    };

    // Counts, numbers and progress run on numbered items: every passage sub-question
    // when the test shows them in the navigator, else one per question.
    const expandPassages = test?.showSubQuestionsInNavigator !== false;
    const numbering = questionNumbering(allQuestions, expandPassages);
    const totalItems = numbering.total;
    const answeredCount = allQuestions.reduce(
        (sum, q) =>
            sum +
            (expandedSubQuestions(q, expandPassages)?.filter((sub) => isSubAnswered(q.id, sub.id)).length ??
                (isAnswered(q.id) ? 1 : 0)),
        0,
    );

    // Section grouping for navigator + current section detection
    const orderedSections = useMemo(
        () => (test ? [...test.sections].sort((a, b) => a.order - b.order) : []),
        [test],
    );

    const sectionMeta = useMemo(() => {
        let cursor = 0;
        return orderedSections.map((section, idx) => {
            const start = cursor;
            cursor += section.questions.length;
            return {
                section,
                start,
                end: cursor,
                idx,
            };
        });
    }, [orderedSections]);

    const currentSectionMeta = useMemo(
        () => sectionMeta.find((s) => currentQuestionIndex >= s.start && currentQuestionIndex < s.end),
        [sectionMeta, currentQuestionIndex],
    );

    // Keyboard navigation (client-only UX): ← → move between questions, 1–9 pick
    // a choice option, F flags. It only ever calls the existing handlers
    // (goToQuestion / toggleFlag / handleAnswerChange), so autosave, the timer,
    // and the violation counter are untouched.
    keyboardHandlerRef.current = (e: KeyboardEvent) => {
        if (!currentQuestion) return;
        // The standardized runner owns the keyboard during IELTS/SAT runs.
        if (runsStandardizedExam) return;
        // Inert while an overlay owns the screen (submit confirm / result / mobile palette).
        if (showSubmitModal || showResultModal || paletteOpen) return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        // A dialog opened from the page (the report dialog) keeps its own keys: F and 1–9 must not flag or answer behind it.
        if (isInsideDialog(e.target)) return;
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
            tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target?.isContentEditable;
        if (isEditable) return;

        const key = e.key;
        if (key === 'ArrowRight' || key === 'ArrowLeft') {
            // Only page between questions when focus is NOT on an interactive
            // control — otherwise the widget owns the arrow keys (Radix radio
            // groups, dnd-kit reorder grips, flag/nav buttons all use them).
            if (target?.closest('button, [role="button"], [role="radiogroup"]')) return;
            if (key === 'ArrowRight') {
                const canAdvance = allowFlexibleNav || isAnswered(currentQuestion.id);
                if (canAdvance && currentQuestionIndex < allQuestions.length - 1) {
                    e.preventDefault();
                    goToQuestion(currentQuestionIndex + 1);
                }
            } else if (allowFlexibleNav && currentQuestionIndex > 0) {
                e.preventDefault();
                goToQuestion(currentQuestionIndex - 1);
            }
            return;
        }

        if (key === 'f' || key === 'F') {
            e.preventDefault();
            toggleFlag(currentQuestion.id);
            return;
        }

        if (/^[1-9]$/.test(key)) {
            const type = currentQuestion.type;
            if (type !== 'MULTIPLE_CHOICE' && type !== 'TRUE_FALSE' && type !== 'MULTIPLE_RESPONSE') {
                return;
            }
            const opts = (currentQuestion.options || []).map((o, i) =>
                typeof o === 'string' ? { id: String(i), value: o } : o,
            );
            const idx = parseInt(key, 10) - 1;
            if (idx < 0 || idx >= opts.length) return;
            e.preventDefault();
            const optionId = opts[idx].id;
            if (type === 'MULTIPLE_RESPONSE') {
                const cur = getAnswer(currentQuestion.id);
                const selectedIds = Array.isArray(cur) ? cur : [];
                const newIds = selectedIds.includes(optionId)
                    ? selectedIds.filter((id) => id !== optionId)
                    : [...selectedIds, optionId];
                chooseOption(currentQuestion.id, optionId, newIds);
            } else {
                chooseOption(currentQuestion.id, optionId, optionId);
            }
        }
    };

    useEffect(() => {
        if (!isStarted) return undefined;
        const onKeyDown = (e: KeyboardEvent) => keyboardHandlerRef.current(e);
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isStarted]);

    if (isLoading) {
        return (
            <div className="ts-shell flex min-h-screen items-center justify-center">
                <div className="space-y-4 text-center">
                    <Loader2 className="mx-auto h-12 w-12 animate-spin" style={{ color: 'var(--ts-primary)' }} />
                    <p className="font-medium text-gray-500">{t('loading_assessment')}</p>
                </div>
                <TestSessionStyles />
            </div>
        );
    }

    if (startError && !isStarted) {
        const isSubError = /subscription/i.test(startError) || /PORTAL_MOCK_LIMIT_REACHED/i.test(startError);
        return (
            <ExamStateCard
                tone={isSubError ? 'warning' : 'error'}
                title={isSubError ? t('subscription_required_for_test') : t('test_not_available')}
                message={startError}
                actions={
                    <>
                        <Button
                            size="lg"
                            variant={isSubError ? 'outline' : 'default'}
                            onClick={() => navigate(backToTestsPath)}
                        >
                            {t('back_to_tests')}
                        </Button>
                        {isSubError && (
                            <Button size="lg" onClick={() => navigate(PORTAL_PLAN_PICKER_PATH)}>
                                {t('buy_subscription')}
                            </Button>
                        )}
                    </>
                }
            />
        );
    }

    const isPlanTest = startInfo?.test?.accessType === 'PLAN';
    const hasActiveSubscription = Boolean(subscription && subscription.mocksRemaining > 0);
    const startAttemptErrorCode = (startAttempt.error as any)?.response?.data?.code;
    const startAttemptErrorMessage =
        (startAttempt.error as any)?.response?.data?.message ||
        (startAttempt.error as any)?.message;
    const isSubscriptionError =
        startAttemptErrorCode === 'PORTAL_MOCK_LIMIT_REACHED' ||
        startAttemptErrorCode === 'PORTAL_SUBSCRIPTION_REQUIRED' ||
        /subscription/i.test(startAttemptErrorMessage || '');
    const needsSubscription = (isPlanTest && !hasActiveSubscription) || isSubscriptionError;

    if (showStartScreen && startInfo && !isStarted) {
        const needsPayment = startInfo.test.priceSom > 0 && !startInfo.test.purchased;
        const startIdentity = [authState.user?.name, authState.user?.email].find((v) => typeof v === 'string' && v.trim()) as string | undefined;
        // A full IELTS/SAT mock's library sections hold no questions of their own.
        const totalQuestions =
            publicQuestionsCount({
                category: startInfo.test.category,
                questionsCount: startInfo.test.totalQuestions,
                sectionsCount: startInfo.test.sectionsCount,
            }) ?? 0;
        return (
            <>
                <TestStartScreen
                    title={startInfo.test.title}
                    description={startInfo.test.description}
                    totalQuestions={totalQuestions}
                    timeLimit={startInfo.test.timeLimit}
                    attemptsUsed={startInfo.attemptsUsed}
                    attemptsAllowed={startInfo.attemptsAllowed}
                    startPage={startInfo.startPage}
                    isStarting={startAttempt.isPending}
                    requiresSubscription={isPlanTest}
                    hasSubscription={hasActiveSubscription}
                    needsSubscription={needsSubscription}
                    onBuySubscription={() => navigate(PORTAL_PLAN_PICKER_PATH)}
                    payment={
                        needsPayment
                            ? { priceSom: startInfo.test.priceSom, balanceSom: authState.user?.balanceSom ?? 0 }
                            : undefined
                    }
                    onBack={() => navigate(backToTestsPath)}
                    aside={
                        normalizeCategorySlug(startInfo.test.category) === 'ielts' ? (
                            <ExamReadinessCard identity={startIdentity} />
                        ) : undefined
                    }
                    notice={
                        paymentOrder ? (
                            <PortalPaymentReturn
                                orderId={paymentOrder}
                                onBalance={setBalance}
                                onDismiss={dismissPaymentReturn}
                                dismissLabel={t('close')}
                            />
                        ) : undefined
                    }
                    error={
                        // A short balance opens the payment step; it is not a start error.
                        startAttempt.isError && startAttemptErrorCode !== 'INSUFFICIENT_BALANCE'
                            ? (planUnavailableText(startAttempt.error, t) ||
                              (startAttempt.error as any)?.response?.data?.message ||
                              (startAttempt.error as any)?.message ||
                              null)
                            : null
                    }
                    onStart={beginSession}
                />
                {paymentOpen && (
                    <TestStartPayment
                        test={{
                            id: startInfo.test.id,
                            title: startInfo.test.title,
                            priceSom: startInfo.test.priceSom,
                            questionsCount: totalQuestions,
                            durationMinutes: startInfo.test.timeLimit ?? undefined,
                            sectionsCount: startInfo.test.sectionsCount,
                        }}
                        onPurchased={handlePurchased}
                        onClose={() => setPaymentOpen(false)}
                    />
                )}
            </>
        );
    }

    if (examFinished && attemptId) {
        return (
            <ExamFinishedScreen
                attemptId={attemptId}
                testTitle={test?.title ?? ''}
                candidateName={authState.user?.name}
                onSeeResult={() => navigate(`${basePath}/results/${attemptId}`, { replace: true })}
                onBack={() => navigate(backToTestsPath)}
            />
        );
    }

    if (runsStandardizedExam && test?.examConfig && attemptId) {
        return (
            <StandardizedExamRunner
                attemptId={attemptId}
                testTitle={test.title}
                examConfig={test.examConfig}
                candidateName={authState.user?.name}
                initialAnswers={new Map([...answers.entries()].map(([id, a]) => [id, a.value]))}
                onFinished={finishStandardizedExam}
            />
        );
    }

    if (!test || allQuestions.length === 0) {
        return (
            <ExamStateCard
                tone="error"
                title={t('test_not_available')}
                message={t(
                    'this_test_could_not_be_loaded_it_may_not_exist_or_you_may_not_have_permission_to_access_it',
                )}
                actions={
                    <Button size="lg" onClick={() => navigate(backToTestsPath)}>
                        {t('back_to_tests')}
                    </Button>
                }
            />
        );
    }

    const progressPct = (answeredCount / totalItems) * 100;
    // Timer states: normal → amber under 5 min → red under 1 min (display only;
    // the countdown + auto-submit-at-0 effect is unchanged).
    const timerDanger = timeRemaining !== null && timeRemaining < 60;
    const timerWarn = timeRemaining !== null && timeRemaining >= 60 && timeRemaining < 300;
    // Start-page meta toggles drive the in-test display. Default (undefined) is
    // shown, so tests that never configured a start page are unaffected.
    const displayMeta = test.startPage?.meta;
    const showTimer = displayMeta?.showTimer !== false;
    const showProgressBar = displayMeta?.showProgressBar !== false;
    const clock = resolveExamClock({
        showTimer,
        perQuestionMode: !!perQuestionSeconds,
        questionTimeRemaining,
        timeUpFlash,
        timeRemaining,
        timerWarn,
        timerDanger,
    });
    const sectionTitle = currentSectionMeta?.section.title ?? null;
    const isPassage = currentQuestion?.type === 'PASSAGE';
    const isFlagged = !!currentQuestion && flaggedQuestions.has(currentQuestion.id);
    const canEliminate =
        !!test?.answerEliminator &&
        (currentQuestion?.type === 'MULTIPLE_CHOICE' || currentQuestion?.type === 'MULTIPLE_RESPONSE');
    const struckOptions = currentQuestion ? eliminated.get(currentQuestion.id) : undefined;
    // Strike state and button for one option row of the current question.
    const eliminatorProps = (optionId: string, index: number) => {
        if (!canEliminate || !currentQuestion) return {};
        const isOut = struckOptions?.has(optionId) ?? false;
        const letter = optionLetter(index);
        return {
            eliminated: isOut,
            onToggleEliminated: eliminatorOn ? () => toggleEliminated(currentQuestion.id, optionId) : undefined,
            eliminateLabel: isOut ? t('exam.undo_eliminate', { letter }) : t('exam.eliminate', { letter }),
        };
    };
    const questionPoints = currentQuestion?.points || 1;
    const prevDisabled = currentQuestionIndex === 0 || !allowFlexibleNav;
    const nextDisabled =
        currentQuestionIndex >= allQuestions.length - 1 ||
        (!allowFlexibleNav && !!currentQuestion && !isAnswered(currentQuestion.id));
    // 1-based numbers of the questions still without an answer (submit dialog chips).
    const unansweredItems = allQuestions.flatMap((q, i) =>
        Array.from({ length: questionItemCount(q, expandPassages) }, (_, j) => {
            const subQuestionId = expandedSubQuestions(q, expandPassages)?.[j].id;
            return {
                number: numbering.starts[i] + j,
                index: i,
                subQuestionId,
                answered: subQuestionId ? isSubAnswered(q.id, subQuestionId) : isAnswered(q.id),
            };
        }).filter((item) => !item.answered),
    );
    const unansweredNumbers = unansweredItems.map((item) => item.number);
    const currentStart = numbering.starts[currentQuestionIndex] ?? currentQuestionIndex + 1;
    const currentEnd = currentStart + (currentQuestion ? questionItemCount(currentQuestion, expandPassages) : 1) - 1;

    const showSaveStatus = !isPreviewMode && !!attemptId;
    const phoneStatusText = isPreviewMode
        ? t('preview')
        : showSaveStatus
          ? saveAnswer.isPending
              ? t('saving')
              : t('saved_just_now')
          : null;

    return (
        // Below lg the runner is a phone screen: sticky header, flat question, sticky bottom bar.
        // Inside the Studio phone shell it is full-bleed: cancel the shell's in-flow padding.
        <div
            ref={shellRef}
            data-surface="exam"
            className="ts ts-shell flex min-h-screen flex-col [[data-mobile-studio]_&]:-mx-4 [[data-mobile-studio]_&]:-mb-[calc(92px+env(safe-area-inset-bottom))] [[data-mobile-studio]_&]:-mt-3"
        >
            <ExamPhoneHeader
                className="lg:hidden"
                title={test.title}
                questionNumber={currentStart}
                totalQuestions={totalItems}
                sectionTitle={sectionTitle}
                statusText={phoneStatusText}
                clock={clock}
                showProgressBar={showProgressBar}
                progressPct={progressPct}
            />
            <div className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col lg:gap-[18px] lg:p-7">
                {/* Top bar + optional full-width proctoring banner */}
                <ExamTopBar
                    className="hidden lg:block"
                    title={test.title}
                    questionNumber={currentStart}
                    totalQuestions={totalItems}
                    sectionTitle={sectionTitle}
                    isPreview={isPreviewMode}
                    showSaveStatus={showSaveStatus}
                    isSaving={saveAnswer.isPending}
                    clock={clock}
                    showProgressBar={showProgressBar}
                    answeredCount={answeredCount}
                    progressPct={progressPct}
                    isSubmitting={isSubmitting}
                    onSubmit={() => handleSubmit(false)}
                />
                {(test.settings?.proctoring?.enabled || test.antiCheating?.antiCheatingMonitor) &&
                    !isPreviewMode && (
                        <div className="px-4 pt-3.5 lg:p-0">
                            <ViolationBanner violationCount={tabSwitchCount} t={t} />
                        </div>
                    )}

                {/* Question + palette rail (a reading passage takes the full width) */}
                <div className={cn('grid items-start gap-[18px]', !isPassage && 'lg:grid-cols-[minmax(0,1fr)_320px]')}>
                    <div className="mx-auto flex w-full min-w-0 max-w-[720px] flex-col gap-3 lg:max-w-none">
                    <motion.div
                        key={currentQuestionIndex}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className={cn(
                            'px-4 pb-6 pt-[18px] lg:rounded-lg lg:border lg:border-border lg:bg-card',
                            isPassage ? 'lg:px-6 lg:py-5' : 'lg:px-8 lg:py-7',
                        )}
                    >
                        <div className="flex items-center justify-between gap-3 lg:items-start lg:gap-4">
                            <div className="flex min-w-0 flex-col gap-2">
                                <span className="hidden text-[13px] font-bold leading-tight text-muted-foreground lg:block">
                                    {currentEnd > currentStart
                                        ? `${t('questions')} ${currentStart}–${currentEnd}`
                                        : `${t('question')} ${currentStart}`}
                                </span>
                                <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
                                    {sectionTitle && <Badge>{sectionTitle}</Badge>}
                                    <span>
                                        {currentQuestion &&
                                            t(QUESTION_TYPE_LABEL_KEY[currentQuestion.type] || 'question')}
                                    </span>
                                    <span aria-hidden="true">·</span>
                                    <span className="tabular-nums">
                                        {questionPoints} {t(questionPoints > 1 ? 'pts' : 'pt')}
                                    </span>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                {/* A real attempt only: a preview has nothing to report against. */}
                                {attemptId && !isPreviewMode && currentQuestion && (
                                    <ReportQuestionButton attemptId={attemptId} questionId={currentQuestion.id} />
                                )}
                                {canEliminate && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        aria-pressed={eliminatorOn}
                                        aria-label={t('exam.sat.eliminator')}
                                        title={t('exam.sat.eliminator')}
                                        className={cn(
                                            'h-10 shrink-0 px-2.5 text-xs font-semibold line-through lg:h-9',
                                            eliminatorOn && 'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background',
                                        )}
                                        onClick={() => setEliminatorOn((on) => !on)}
                                    >
                                        ABC
                                    </Button>
                                )}
                                {/* 40px icon button on phones, labelled 36px button on desktop */}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    aria-pressed={isFlagged}
                                    className="h-10 w-10 shrink-0 gap-1.5 p-0 lg:h-9 lg:w-auto lg:px-3.5"
                                    onClick={() => currentQuestion && toggleFlag(currentQuestion.id)}
                                >
                                    <Flag
                                        className={cn('h-4 w-4', isFlagged && 'fill-warning-500 text-warning-500')}
                                        aria-hidden
                                    />
                                    <span className="sr-only lg:not-sr-only">{isFlagged ? t('flagged') : t('flag')}</span>
                                </Button>
                            </div>
                        </div>

                        {currentQuestion && !PROMPT_OWNED_TAKE_TYPES.has(currentQuestion.type) && (
                            <div className="mt-4 select-none text-[17px] font-semibold leading-[1.5] text-foreground lg:mt-[22px] lg:max-w-[720px] lg:text-[19px] lg:leading-[1.45] lg:tracking-[-0.01em]">
                                <RenderedHtml html={currentQuestion.prompt || ''} />
                            </div>
                        )}

                        {currentQuestion && (
                            <div className="mt-5 flex flex-col gap-2.5 lg:mt-6">
                                {(currentQuestion.type === 'MULTIPLE_CHOICE' ||
                                    currentQuestion.type === 'TRUE_FALSE') && (() => {
                                        const opts = (currentQuestion.options || []).map((o, i) =>
                                            typeof o === 'string' ? { id: String(i), value: o } : o,
                                        );
                                        const currentAnswer = getAnswer(currentQuestion.id);
                                        const selectedId =
                                            typeof currentAnswer === 'string' ? currentAnswer : '';
                                        return (
                                            <RadioGroup
                                                value={selectedId}
                                                onValueChange={(value) =>
                                                    chooseOption(currentQuestion.id, value, value)
                                                }
                                                aria-label={t('answer_options')}
                                                className="flex flex-col gap-2.5"
                                            >
                                                {opts.map((option, index) => {
                                                    const optionId = option.id;
                                                    const itemId = `q-${currentQuestion.id}-${optionId}`;
                                                    return (
                                                        <ExamOptionRow
                                                            key={optionId}
                                                            htmlFor={itemId}
                                                            letter={optionLetter(index)}
                                                            selected={selectedId === optionId}
                                                            {...eliminatorProps(optionId, index)}
                                                            control={
                                                                <RadioGroupItem
                                                                    id={itemId}
                                                                    value={optionId}
                                                                    className={EXAM_RADIO_CLASS}
                                                                />
                                                            }
                                                        >
                                                            <RenderedHtml html={getQuestionOptionLabel(currentQuestion.type, option.value || '', t)} inline />
                                                        </ExamOptionRow>
                                                    );
                                                })}
                                            </RadioGroup>
                                        );
                                    })()}

                                {currentQuestion.type === 'MULTIPLE_RESPONSE' && (
                                    <div role="group" aria-label={t('answer_options')} className="flex flex-col gap-2.5">
                                        {(currentQuestion.options || [])
                                            .map((o, i) =>
                                                typeof o === 'string' ? { id: String(i), value: o } : o,
                                            )
                                            .map((option, index) => {
                                                const optionId = option.id;
                                                const optionValue = option.value;
                                                const currentAnswer = getAnswer(currentQuestion.id);
                                                const selectedIds = Array.isArray(currentAnswer)
                                                    ? currentAnswer
                                                    : [];
                                                const isSelected = selectedIds.includes(optionId);
                                                const itemId = `q-${currentQuestion.id}-${optionId}`;
                                                return (
                                                    <ExamOptionRow
                                                        key={optionId}
                                                        htmlFor={itemId}
                                                        letter={optionLetter(index)}
                                                        selected={isSelected}
                                                        {...eliminatorProps(optionId, index)}
                                                        control={
                                                            <Checkbox
                                                                id={itemId}
                                                                checked={isSelected}
                                                                onCheckedChange={() => {
                                                                    const newIds = isSelected
                                                                        ? selectedIds.filter((id) => id !== optionId)
                                                                        : [...selectedIds, optionId];
                                                                    chooseOption(currentQuestion.id, optionId, newIds);
                                                                }}
                                                                className={EXAM_CHECKBOX_CLASS}
                                                            />
                                                        }
                                                    >
                                                        <RenderedHtml html={optionValue || ''} inline />
                                                    </ExamOptionRow>
                                                );
                                            })}
                                    </div>
                                )}

                                {currentQuestion.maxWords && <p className="mb-3 text-sm text-muted-foreground">{t('reading_word_limit', { count: currentQuestion.maxWords })}</p>}
                                {currentQuestion.type === 'SHORT_ANSWER' && (
                                    <Input
                                        aria-label={t('answer')}
                                        placeholder={t('type_your_answer_here')}
                                        value={(getAnswer(currentQuestion.id) as string) || ''}
                                        onChange={(e) =>
                                            handleAnswerChange(currentQuestion.id, e.target.value)
                                        }
                                        className="min-h-11 text-sm"
                                    />
                                )}

                                {currentQuestion.type === 'MATCHING' && <MatchingPlayer
                                    pairs={currentQuestion.matchPairs ?? []} options={currentQuestion.matchingOptions}
                                    value={(getAnswer(currentQuestion.id) as Record<string, string>) || {}}
                                    onChange={(value) => handleAnswerChange(currentQuestion.id, value)} />}

                                {/* Fill in the Blanks */}
                                {currentQuestion.type === 'FILL_IN_BLANKS' && currentQuestion.blanks && (
                                    <FillInBlanksPlayer
                                        prompt={currentQuestion.prompt || ''}
                                        blanks={currentQuestion.blanks}
                                        value={(getAnswer(currentQuestion.id) as Record<string, string>) || {}}
                                        onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                                    />
                                )}

                                {/* Open Ended */}
                                {currentQuestion.type === 'OPEN_ENDED' && (
                                    <OpenEndedPlayer
                                        value={(getAnswer(currentQuestion.id) as string) || ''}
                                        onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                                        rubric={currentQuestion.rubric}
                                    />
                                )}

                                {/* Student-produced response (SAT grid-in) */}
                                {currentQuestion.type === 'SPR' && (
                                    <SprInput
                                        value={(getAnswer(currentQuestion.id) as string) || ''}
                                        onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                                    />
                                )}

                                {/* Reorder */}
                                {currentQuestion.type === 'REORDER' && currentQuestion.reorderItems && (
                                    <ReorderPlayer
                                        items={currentQuestion.reorderItems}
                                        value={(getAnswer(currentQuestion.id) as string[]) || []}
                                        onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                                    />
                                )}

                                {/* Passage: reading text + sub-questions */}
                                {currentQuestion.type === 'PASSAGE' && (
                                    <PassagePlayer
                                        passageText={currentQuestion.passageText || ''}
                                        passageTitle={currentQuestion.passageTitle}
                                        subQuestions={currentQuestion.subQuestions ?? []}
                                        pages={currentQuestion.pages}
                                        numberOffset={expandPassages ? currentStart - 1 : 0}
                                        focusRequest={subQuestionFocus}
                                        value={(getAnswer(currentQuestion.id) as PassageAnswer) || {}}
                                        onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                                        renderPassage={(passage, pageIndex) => {
                                            const key = `${currentQuestion.id}:${pageIndex}`;
                                            return (
                                                <PassageHighlighter
                                                    contentKey={key}
                                                    highlights={passageHighlights.get(key)}
                                                    onChange={(change) => passageHighlights.update(key, change)}
                                                >
                                                    {passage}
                                                </PassageHighlighter>
                                            );
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {/* Desktop footer; phones use the bottom bar */}
                        <div className="mt-7 hidden items-center justify-between gap-4 border-t border-border pt-[18px] lg:flex">
                            <Button
                                type="button"
                                variant="outline"
                                size="default"
                                className="gap-1.5 px-[18px]"
                                onClick={() => goToQuestion(currentQuestionIndex - 1)}
                                disabled={prevDisabled}
                            >
                                <ChevronLeft className="h-4 w-4" aria-hidden />
                                {t('exam.nav.previous')}
                            </Button>
                            <span className="min-w-0 flex-1 truncate text-center text-[13px] tabular-nums text-muted-foreground">
                                {isPreviewMode
                                    ? t('preview')
                                    : t('exam_answered_summary', {
                                          answered: answeredCount,
                                          flagged: flaggedQuestions.size,
                                          left: totalItems - answeredCount,
                                      })}
                            </span>
                            <Button
                                type="button"
                                variant="ink"
                                size="default"
                                className="gap-1.5 px-[18px]"
                                onClick={() => goToQuestion(currentQuestionIndex + 1)}
                                disabled={nextDisabled}
                            >
                                {t('next')}
                                <ChevronRight className="h-4 w-4" aria-hidden />
                            </Button>
                        </div>
                    </motion.div>
                        <p aria-hidden="true" className="hidden text-xs text-muted-foreground lg:block">
                            {t('exam_kbd_hint')}
                        </p>
                    </div>

                    {/* Sticky palette rail */}
                    {!isPassage && (
                        <aside className="sticky top-[18px] hidden rounded-lg border border-border bg-card px-[18px] py-4 lg:block">
                            <PaletteDrawer
                                sectionMeta={sectionMeta}
                                answeredCount={answeredCount}
                                totalQuestions={totalItems}
                                currentQuestionIndex={currentQuestionIndex}
                                isAnswered={isAnswered}
                                flaggedQuestions={flaggedQuestions}
                                allowFlexibleNav={allowFlexibleNav}
                                expandPassages={expandPassages}
                                isSubAnswered={isSubAnswered}
                                currentSubQuestionId={subQuestionFocus?.questionId}
                                onGoToQuestion={goToQuestion}
                            />
                        </aside>
                    )}
                </div>
            </div>

            {/* Phones: bottom bar (previous · palette · next) and the palette sheet. */}
            <ExamPhoneNav
                className="lg:hidden"
                answeredCount={answeredCount}
                totalQuestions={totalItems}
                prevDisabled={prevDisabled}
                nextDisabled={nextDisabled}
                onPrev={() => goToQuestion(currentQuestionIndex - 1)}
                onNext={() => goToQuestion(currentQuestionIndex + 1)}
                onOpenPalette={() => setPaletteOpen(true)}
                paletteOpen={paletteOpen}
            />
            <PaletteSheet
                open={paletteOpen}
                onOpenChange={setPaletteOpen}
                sectionMeta={sectionMeta}
                answeredCount={answeredCount}
                totalQuestions={totalItems}
                currentQuestionIndex={currentQuestionIndex}
                isAnswered={isAnswered}
                flaggedQuestions={flaggedQuestions}
                allowFlexibleNav={allowFlexibleNav}
                expandPassages={expandPassages}
                isSubAnswered={isSubAnswered}
                currentSubQuestionId={subQuestionFocus?.questionId}
                onGoToQuestion={(index, subQuestionId) => {
                    goToQuestion(index, subQuestionId);
                    setPaletteOpen(false);
                }}
                isSubmitting={isSubmitting}
                onReviewAndSubmit={() => {
                    setPaletteOpen(false);
                    handleSubmit(false);
                }}
            />

            {/* Submit confirmation */}
            <SubmitDialog
                open={showSubmitModal}
                onCancel={() => setShowSubmitModal(false)}
                onConfirm={() => handleSubmit(true)}
                isPreview={isPreviewMode}
                isSubmitting={isSubmitting}
                answeredCount={answeredCount}
                totalQuestions={totalItems}
                flaggedCount={flaggedQuestions.size}
                unansweredNumbers={unansweredNumbers}
                targetOf={(number) => {
                    const item = unansweredItems.find((candidate) => candidate.number === number);
                    return { index: item?.index ?? number - 1, subQuestionId: item?.subQuestionId };
                }}
                // Same rule as the palette: forced-linear runs only reopen the current question.
                canGoToQuestion={(index) => allowFlexibleNav || index === currentQuestionIndex}
                onGoToQuestion={(index, subQuestionId) => {
                    goToQuestion(index, subQuestionId);
                    setShowSubmitModal(false);
                }}
            />

            {/* Result (regular, non-guest runs and previews; guests go to /r/:resultToken) */}
            <ResultDialog
                open={showResultModal && !!attemptResult && !sessionEnd.blocking}
                onClose={() => {
                    setShowResultModal(false);
                    navigate(
                        attemptResult?.preview && testId ? `/app/tests/edit/${testId}` : backToTestsPath,
                    );
                }}
                preview={Boolean(attemptResult?.preview)}
                score={attemptResult?.score}
                maxScore={attemptResult?.maxScore}
                needsManualGrading={attemptResult?.status === 'SUBMITTED'}
                isMarketplace={isMarketplaceTest(test)}
                passingScore={test.settings?.passingScore}
                category={test.category}
                testTitle={test.title}
                tags={test.tags}
                subjectConfigsCount={test.subjectConfigs?.length}
                scaledReport={attemptResult?.scaledReport}
                userGoalLevel={
                    authState.user?.learnerProfile?.goals?.find((g) => {
                        const cat = (test.category || '').toLowerCase();
                        if (cat.includes('milliy') && g.exam === 'MILLIY_SERTIFIKAT') return true;
                        if (cat === 'dtm' && g.exam === 'DTM') return true;
                        if (cat === 'ielts' && g.exam === 'IELTS') return true;
                        if (cat === 'sat' && g.exam === 'SAT') return true;
                        if ((cat === 'attestation' || cat === 'attestatsiya') && g.exam === 'ATTESTATSIYA') return true;
                        return false;
                    })?.level
                }
                onViewDetails={() => {
                    setShowResultModal(false);
                    navigate(`${basePath}/results/${attemptId}`);
                }}
                onBackToTests={() => {
                    setShowResultModal(false);
                    navigate(backToTestsPath);
                }}
                onBackToAssessment={() => {
                    setShowResultModal(false);
                    navigate(testId ? `/app/tests/edit/${testId}` : '/app/tests');
                }}
            />

            <SessionEndDialogs flow={sessionEnd} />

            <TestSessionStyles />
        </div>
    );
};

const TestSessionStyles: React.FC = () => (
    <style>{`
.ts-shell {
    --ts-primary: #171717;
    --ts-bg: #F4F5F7;
    --ts-card-bg: #FFFFFF;
    --ts-gray-200: #E5E7EB;
    --ts-gray-900: #111827;
    --ts-warn-bg: #FFFBEB;
    --ts-warn-border: #FDE68A;
    --ts-warn-text: #92400E;
    --ts-warn-text-soft: #B45309;
    background: var(--ts-bg);
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--ts-gray-900);
}
.dark .ts-shell {
    --ts-primary: #FAFAFA;
    --ts-bg: hsl(var(--background));
    --ts-card-bg: hsl(var(--card));
    --ts-gray-200: hsl(var(--border));
    --ts-gray-900: hsl(var(--foreground));
    --ts-warn-bg: rgba(245, 158, 11, 0.14);
    --ts-warn-border: rgba(245, 158, 11, 0.4);
    --ts-warn-text: #FCD34D;
    --ts-warn-text-soft: #FBBF24;
}
.ts h1, .ts h2, .ts h3, .ts h4 { margin: 0; }
.ts-card {
    background: var(--ts-card-bg);
    border: 1px solid var(--ts-gray-200);
    border-radius: 12px;
}
.ts-violation-banner {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px;
    background: var(--ts-warn-bg);
    border-color: var(--ts-warn-border);
    color: var(--ts-warn-text-soft);
}
.ts-violation-banner-icon {
    display: inline-flex; flex-shrink: 0;
    color: var(--ts-warn-text-soft);
}
.ts-violation-banner-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.ts-violation-banner-title { font-size: 13px; font-weight: 600; line-height: 1.35; }
.ts-violation-banner-sub {
    font-size: 11.5px; color: var(--ts-warn-text);
    font-weight: 600; font-variant-numeric: tabular-nums;
}
`}</style>
);

export default TestTaking;
